/* eslint-disable no-unused-vars */
import { AlertTriangle, CheckCircle2, X, XCircle } from 'lucide-react';
import React, { createContext, useCallback, useContext, useRef, useState } from 'react';

// ── Confirm ───────────────────────────────────────────────────────────────────

type ConfirmOptions = {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
};

type PendingConfirm = ConfirmOptions & {
  resolve: (confirmed: boolean) => void;
};

// ── Toast ─────────────────────────────────────────────────────────────────────

type ToastKind = 'success' | 'error';

type Toast = {
  id: number;
  kind: ToastKind;
  message: string;
};

const TOAST_DURATION_MS = 4200;

type FeedbackContextValue = {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  toast: (kind: ToastKind, message: string) => void;
};

const FeedbackContext = createContext<FeedbackContextValue | null>(null);

function FeedbackProvider({ children }: { children: React.ReactNode }) {
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastId = useRef(0);

  const confirm = useCallback((options: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setPendingConfirm({ ...options, resolve });
    });
  }, []);

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback((kind: ToastKind, message: string) => {
    const id = ++toastId.current;
    setToasts((prev) => [...prev.slice(-3), { id, kind, message }]);
    window.setTimeout(() => dismissToast(id), TOAST_DURATION_MS);
  }, [dismissToast]);

  const settleConfirm = (confirmed: boolean) => {
    pendingConfirm?.resolve(confirmed);
    setPendingConfirm(null);
  };

  return (
    <FeedbackContext.Provider value={{ confirm, toast }}>
      {children}

      {pendingConfirm ? (
        <div className="dialogOverlay" role="presentation" onClick={() => settleConfirm(false)}>
          <div
            className="dialogPanel"
            style={{ width: 'min(440px, 100%)' }}
            role="alertdialog"
            aria-modal="true"
            aria-label={pendingConfirm.title}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="dialogHeader">
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                {pendingConfirm.danger ? (
                  <span style={{ color: 'var(--red)', marginTop: 4, flexShrink: 0 }}>
                    <AlertTriangle size={20} />
                  </span>
                ) : null}
                <div>
                  <h3 style={{ margin: 0, fontSize: 18 }}>{pendingConfirm.title}</h3>
                  <p style={{ margin: '8px 0 0', fontSize: 13.5, color: 'var(--text-soft)', whiteSpace: 'pre-line' }}>
                    {pendingConfirm.message}
                  </p>
                </div>
              </div>
              <button type="button" className="iconButton" onClick={() => settleConfirm(false)} aria-label="Cancel">
                <X size={16} />
              </button>
            </div>
            <div className="dialogActions">
              <button type="button" className="secondaryButton" onClick={() => settleConfirm(false)} autoFocus>
                {pendingConfirm.cancelLabel || 'Cancel'}
              </button>
              <button
                type="button"
                className={pendingConfirm.danger ? 'dangerButton' : 'primaryButton'}
                onClick={() => settleConfirm(true)}
              >
                {pendingConfirm.confirmLabel || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {toasts.length > 0 ? (
        <div className="toastStack" role="status" aria-live="polite">
          {toasts.map((t) => (
            <div key={t.id} className={`toast toast-${t.kind}`}>
              <span className="toastIcon">
                {t.kind === 'success' ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
              </span>
              <span className="toastMessage">{t.message}</span>
              <button type="button" className="toastClose" onClick={() => dismissToast(t.id)} aria-label="Dismiss">
                <X size={13} />
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </FeedbackContext.Provider>
  );
}

function useFeedback(): FeedbackContextValue {
  const ctx = useContext(FeedbackContext);
  if (!ctx) throw new Error('useFeedback must be used within FeedbackProvider');
  return ctx;
}

export { FeedbackProvider, useFeedback };
export type { ConfirmOptions };
